import { AlertTriangle, Package } from "@/components/icons";
import { Skeleton } from "@/components/kit";
import type { StockValuationDTO } from "@/modules/reports/domain/types";
import { formatLak } from "@/shared/lib/currency";

type StockOverviewStripProps = {
  valuation?: StockValuationDTO;
  valuationLoading?: boolean;
  agingCount?: number;
  agingLoading?: boolean;
  deadCount?: number;
  deadLoading?: boolean;
  onGoValuation: () => void;
  onGoAging: () => void;
  onGoDead: () => void;
};

export function StockOverviewStrip({
  valuation,
  valuationLoading,
  agingCount = 0,
  agingLoading,
  deadCount = 0,
  deadLoading,
  onGoValuation,
  onGoAging,
  onGoDead,
}: StockOverviewStripProps) {
  if (valuationLoading) {
    return <Skeleton className="h-28 w-full rounded-2xl" />;
  }

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={onGoValuation}
        className="brand-card flex w-full items-center gap-3 p-4 text-left transition-colors hover:bg-muted/40"
      >
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-secondary">
          <Package />
        </div>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-muted-foreground text-xs">ມູນຄ່າຄລັງລວມ</span>
          <span className="font-display text-2xl tabular-nums leading-tight">
            {formatLak(valuation?.totalInventoryValueLak ?? "0")}
          </span>
          <span className="text-muted-foreground text-xs">
            ຄົງເຫຼືອ {formatLak(valuation?.onHandValueLak ?? "0")} · ລະຫວ່າງທາງ{" "}
            {formatLak(valuation?.inTransitValueLak ?? "0")}
          </span>
        </div>
      </button>

      <div className="grid gap-2 sm:grid-cols-2">
        <AttentionChip
          loading={agingLoading}
          count={agingCount}
          label="lot ຄ້າງສະຕັອກ"
          emptyLabel="ບໍ່ມີ lot ຄ້າງຕາມເງື່ອນໄຂ"
          onClick={onGoAging}
        />
        <AttentionChip
          loading={deadLoading}
          count={deadCount}
          label="ສິນຄ້າຄ້າງ / ຂາຍຊ້າ"
          emptyLabel="ບໍ່ມີສິນຄ້າຄ້າງຕາມເງື່ອນໄຂ"
          onClick={onGoDead}
        />
      </div>

      <p className="text-muted-foreground text-xs">
        ມູນຄ່າຄລັງ = ຄົງເຫຼືອ + ລະຫວ່າງທາງ (ຕົ້ນທຶນ lot)
      </p>
    </div>
  );
}

function AttentionChip({
  loading,
  count,
  label,
  emptyLabel,
  onClick,
}: {
  loading?: boolean;
  count: number;
  label: string;
  emptyLabel: string;
  onClick: () => void;
}) {
  if (loading) {
    return <Skeleton className="h-14 rounded-xl" />;
  }

  const hasItems = count > 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted/40 ${
        hasItems
          ? "border-amber-500/30 bg-amber-500/5"
          : "border-border bg-muted/20"
      }`}
    >
      <AlertTriangle
        className={
          hasItems
            ? "text-amber-700 dark:text-amber-400"
            : "text-muted-foreground"
        }
      />
      <span className="min-w-0">
        {hasItems ? (
          <>
            <span className="font-medium tabular-nums">{count}</span> {label}
          </>
        ) : (
          <span className="text-muted-foreground">{emptyLabel}</span>
        )}
      </span>
    </button>
  );
}
